import { ReactNode, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { Chat, PatientList } from 'components';
import { isHipaaLink } from '../../config/productProfile';

import styles from './PatientQueue.module.scss';

type PatientQueueProps = {
  className?: string;
  additionalMenuSlot?: ReactNode;
};

export function PatientQueue({ className, additionalMenuSlot }: PatientQueueProps) {
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const user = useAppSelector(selectUser);
  const roomName = user?.clinicUser?.roomName || '';
  const [isCallStarting, setIsCallStarting] = useState(false);
  return (
    <Stack
      direction='column'
      alignItems='center'
      justifyContent='flex-start'
      sx={{
        gap: '24px',
        ...(additionalMenuSlot && { paddingBottom: '24px' }),
      }}
    >
      <Card
        className={classnames(styles.patientQueue, className)}
        sx={(theme) => ({
          backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : 'var(--app-queue-surface)',
          boxShadow: theme.palette.mode === 'dark' ? 'none' : 'rgba(0, 0, 0, 0.05) 0px 2px 4px',

          [theme.breakpoints.down(900)]: {
            width: '100% !important',
          },
        })}
      >
        <CardHeader
          className={styles.clientHeader}
          sx={(theme) => ({
            backgroundColor: theme.palette.mode === 'dark' ? 'var(--app-header-bg)' : 'var(--app-queue-header-bg)',
          })}
          title={isHipaaLink ? t('clientQueue') : t('waitingRoom')}
          subheader={
            roomName ? (
              <Typography
                variant='body2'
                sx={(theme) => ({
                  fontSize: '0.75rem',
                  color: theme.palette.text.secondary,
                  lineHeight: 1.2,
                  textTransform: 'none',
                  direction: 'ltr',
                  unicodeBidi: 'isolate',
                  display: 'inline-block',
                  textAlign: 'left',
                  marginInlineStart: isRtl ? 'auto' : 0,
                })}
              >
                /{roomName.toLowerCase()}
              </Typography>
            ) : null
          }
        />
        <CardContent
          className={classnames(styles.cardContent, {
            [styles.tabletCardContent]: isTablet,
            [styles.mobileCardContent]: isMobile,
          })}
        >
          {isCallStarting && (
            <Box className={styles.spinnerOverlay}>
              <CircularProgress className={styles.spinner} />
            </Box>
          )}
          <PatientList onCallStartingChange={setIsCallStarting} />
        </CardContent>

        <Chat className={styles.chat} />
      </Card>
      {additionalMenuSlot && <Box sx={{ width: '100%' }}>{additionalMenuSlot}</Box>}
    </Stack>
  );
}
